import { useSphereDialog } from "@components/common/dialog/sphere-dialog-provider";
import { WorldMapMarkerIntegrationIcon } from "@components/common/icons";
import { MenuItemOption } from "@components/common/mobile/mobile-types";
import { ProjectLocationEditorMenuItem } from "@components/project-location-editor/project-location-editor-menu-item";
import { DeleteMenuItem } from "@components/project/delete-menu-item";
import { WorldMapHandle } from "@components/world-map/world-map-types";
import { isSdbProjectWithPosition } from "@custom-types/project-type-guards";
import { ProjectArchivingState, SdbProject } from "@custom-types/project-types";
import { ProjectLaunchTarget } from "@custom-types/sdb-company-types";
import {
  ArchiveIcon,
  DeleteIcon,
  HolobuilderIcon,
  InfoIcon,
  ManagementIcon,
  SelectionIcon,
  ShareLinkIcon,
  UnarchiveIcon,
  ViewerIcon,
  blue,
} from "@flat-ui";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useMediaQueryList } from "@hooks/use-media-query";
import { ChangeManagementButtonMobile } from "@pages/project-details/project-overview/change-management/change-management-button-mobile";
import { isAllowedToChangeManagement } from "@pages/project-details/project-overview/change-management/change-management-utils";
import { generateProjectDetailsRoute } from "@router/router-helper";
import { useCompanyId } from "@context-providers/company-id-provider";
import { commonStrings } from "@src/constants/common-strings";
import { appStateSelector } from "@store/app/app-selector";
import { defaultProjectLaunchTargetSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { selectedRowIdsSelector } from "@store/table/table-selector";
import { setInitialFetchingItems } from "@store/table/table-slice";
import { dataViewModeSelector } from "@store/ui/ui-selector";
import { toggleItemSelection } from "@utils/array-utils";
import { hasUserValidFeatureProjectLevel } from "@utils/feature-control/project/project-feature-control";
import { RequiredFeatureProjectLevelName } from "@utils/feature-control/project/project-feature-control-types";
import {
  hasDescribeOnlyPermission,
  hasUserValidPermissionProjectLevel,
} from "@utils/permission-control/project-permission-control";
import { RequiredPermissionProjectLevelName } from "@utils/permission-control/project-permission-control-types";
import { showArchiveProjectDialog } from "@utils/project-archive-utils";
import {
  getOpenProjectHref,
  getProjectArchivingState,
  isFeatureEnabled,
  isProjectDemo,
  isProjectDownloaded,
  trackOpenProject,
  trackOpenProjectDetails,
} from "@utils/project-utils";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { ProjectActionContext } from "@components/common/project-actions";
import { ShareProjectOption } from "@pages/projects/share-project-option";

interface Props {
  /** Determines on which context the project menu is being used */
  contextType: ProjectActionContext;

  /** Callback to navigate to a project position on the projects map */
  goTo?: WorldMapHandle["goTo"];
}

/**
 * Custom hook to get the menu options for a project.
 * Provides actions for viewing details, opening in Sphere XG/HoloBuilder, access level, archiving, sharing and management
 */
export function useProjectMenuOptions({
  contextType,
  goTo,
}: Props): MenuItemOption<SdbProject>[] {
  const companyId = useCompanyId();
  const dispatch = useAppDispatch();
  const selectedRowIds = useAppSelector(selectedRowIdsSelector);

  const { trackEvent, trackAsyncEvent } = useTrackEvent();
  const { createDialog } = useSphereDialog();

  const projectLaunchSettings = useAppSelector(
    defaultProjectLaunchTargetSelector
  );
  const { isScreenSmOrSmaller } = useMediaQueryList();
  const { projects: viewMode } = useAppSelector(dataViewModeSelector);
  const { isAlphaTestingEnabled } = useAppSelector(appStateSelector);

  const isMapView = viewMode === "map" && isAlphaTestingEnabled;

  const { canEditAllCompanyProjects } = useHasUserValidRoleCompanyLevel();

  /**
   * Whether project selection action should be available.
   * For tiles or list view only enable it for mobile view (small screens).
   * For map view the action should always be visible.
   * TODO - Remove check for alpha flag: https://faro01.atlassian.net/browse/ST-3944
   */
  const shouldShowProjectSelection = isScreenSmOrSmaller || isMapView;

  /** Tracks and opens a project in the given launch target */
  async function trackAndOpenProject(
    project: SdbProject,
    openTarget: ProjectLaunchTarget
  ): Promise<void> {
    await trackOpenProject({
      trackAsyncEvent,
      openFrom: contextType,
      numberOfMembers: project.members.length,
      openTarget,
      ...project,
    });

    window.open(
      getOpenProjectHref({ projectId: project.id, openTarget }),
      "_self"
    );
  }

  /** Opens the archive/unarchive confirmation dialog for the given project. */
  async function handleArchiveAction(project: SdbProject): Promise<void> {
    await showArchiveProjectDialog({
      project,
      createDialog,
      dispatch,
      trackEvent,
    });
  }

  /** Whether the given project is archived, derived from its data (not the URL). */
  function isProjectArchived(project: SdbProject): boolean {
    return (
      getProjectArchivingState(project.archivingState) ===
      ProjectArchivingState.archived
    );
  }

  const menuOptions: MenuItemOption<SdbProject>[] = [
    {
      label: "Select project",
      Icon: SelectionIcon,
      onSelect: (project) => {
        const newSelectedRowIds = toggleItemSelection(
          selectedRowIds,
          project.id
        );
        dispatch(setInitialFetchingItems(newSelectedRowIds));
      },
      shouldShow: () => shouldShowProjectSelection,
      shouldShowDividerAfter: shouldShowProjectSelection,
    },
    {
      label: "View details",
      Icon: InfoIcon,
      onSelect: (project) => {
        trackOpenProjectDetails({
          projectId: project.id,
          trackEvent,
          props: {
            eventLocation: getProjectArchivingState(project.archivingState),
            viewMode,
          },
        });
      },
      getHrefUrl: (project) => {
        return generateProjectDetailsRoute(companyId, project.id);
      },
    },
    {
      label: "Go to",
      Icon: WorldMapMarkerIntegrationIcon,
      menuItemSx: {
        "& svg": { color: "inherit" },
        "&:hover svg": { color: blue[500] },
      },
      onSelect: (project) => project.position && goTo?.(project.position),
      shouldShow: (project) =>
        isMapView && goTo !== undefined && isSdbProjectWithPosition(project),
    },
    {
      label: "Edit location",
      // eslint-disable-next-line react/jsx-no-useless-fragment -- Icon is rendered inside ProjectLocationEditorMenuItem
      Icon: () => <></>,
      component: (project, closeMenu) => (
        <ProjectLocationEditorMenuItem
          project={project}
          closeMenu={closeMenu}
        />
      ),
      shouldShow: (project) => {
        const hasPermissions =
          canEditAllCompanyProjects ||
          hasUserValidPermissionProjectLevel({
            permissionName:
              RequiredPermissionProjectLevelName.canEditProjectDetails,
            selectedProject: project,
          });

        // Show "Edit location" action only for users with permissions and only in projects map view.
        return hasPermissions && isMapView;
      },
    },
    {
      label: isScreenSmOrSmaller
        ? commonStrings.SPHERE_XG
        : `Open in ${commonStrings.SPHERE_XG}`,
      Icon: ViewerIcon,
      onSelect: async (project) => {
        await trackAndOpenProject(project, ProjectLaunchTarget.sphereViewer);
      },
      getHrefUrl: (project) => {
        return getOpenProjectHref({
          projectId: project.id,
          openTarget: ProjectLaunchTarget.sphereViewer,
        });
      },
      shouldHandleNavigation: true,
      shouldShow: (project) => !hasDescribeOnlyPermission(project),
    },
    {
      label: isScreenSmOrSmaller
        ? commonStrings.HOLOBUILDER_PRODUCT_NAME
        : `Open in ${commonStrings.HOLOBUILDER_PRODUCT_NAME}`,
      Icon: HolobuilderIcon,
      onSelect: async (project) => {
        await trackAndOpenProject(project, ProjectLaunchTarget.webEditor);
      },
      getHrefUrl: (project) => {
        return getOpenProjectHref({
          projectId: project.id,
          openTarget: ProjectLaunchTarget.webEditor,
        });
      },
      shouldHandleNavigation: true,
      shouldShow: (project) => {
        return (
          projectLaunchSettings.shouldShowOpenInWebEditorBtn &&
          !hasDescribeOnlyPermission(project)
        );
      },
      shouldShowDividerAfter: true,
    },
    {
      label: "Management",
      Icon: ManagementIcon,
      component: (project, onMenuClose) => (
        <ChangeManagementButtonMobile project={project} onClose={onMenuClose} />
      ),
      shouldShow: (project) => {
        return (
          canEditAllCompanyProjects ||
          (isAllowedToChangeManagement(isProjectDemo(project), project) &&
            !isProjectArchived(project))
        );
      },
    },
    {
      label: "Archive",
      Icon: ArchiveIcon,
      onSelect: handleArchiveAction,
      shouldShow: (project) => {
        if (isProjectArchived(project)) {
          return false;
        }

        const hasFeatureRole = hasUserValidFeatureProjectLevel({
          roleName: RequiredFeatureProjectLevelName.canArchiveProject,
          featuresAvailable: project.featuresAvailable ?? undefined,
        });

        return (
          canEditAllCompanyProjects ||
          (hasFeatureRole && isFeatureEnabled(project, "project-archive"))
        );
      },
      shouldShowDividerAfter: true,
    },
    {
      label: "Unarchive",
      Icon: UnarchiveIcon,
      onSelect: handleArchiveAction,
      shouldShow: (project) => {
        if (!isProjectArchived(project) || isProjectDownloaded(project)) {
          return false;
        }

        const hasFeatureRole = hasUserValidFeatureProjectLevel({
          roleName: RequiredFeatureProjectLevelName.canUnarchiveProject,
          featuresAvailable: project.featuresAvailable ?? undefined,
        });

        return (
          canEditAllCompanyProjects ||
          (hasFeatureRole && isFeatureEnabled(project, "project-unarchive"))
        );
      },
      shouldShowDividerAfter: true,
    },
    {
      label: "Share",
      Icon: ShareLinkIcon,
      component: (project) => <ShareProjectOption project={project} />,
      shouldShow: (project) => !hasDescribeOnlyPermission(project),
    },
    // Delete (archived only)
    {
      label: "Delete",
      Icon: DeleteIcon,
      component: (project) => (
        <DeleteMenuItem type="project" entity={project} />
      ),
      shouldShow: (project) => {
        const hasPermissions =
          canEditAllCompanyProjects ||
          hasUserValidPermissionProjectLevel({
            permissionName: RequiredPermissionProjectLevelName.canDeleteProject,
            selectedProject: project,
          });

        return hasPermissions && isProjectArchived(project);
      },
    },
  ];

  return menuOptions;
}
